import { useState, useMemo } from 'react';
import { TrendAreaChart } from '@/components/charts/TrendAreaChart';
import { ComparisonBars } from '@/components/charts/ComparisonBars';
import { AgeDonut } from '@/components/charts/AgeDonut';
import { SegmentChips } from '@/components/common/SegmentChips';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { useSegmentQueries } from '@/lib/useSegment';
import { SEG_META } from '@/lib/segments';
import { fmtInt, fmtNum } from '@/lib/format';

function tickDate(p) {
  if (typeof p === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(p)) {
    return new Date(p).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  }
  return p;
}

// ---------- Évolution des signatures (BS validés) superposée par famille ----------
export function SegmentTrend({ body, seuil }) {
  const [segs, setSegs] = useState(['ensemble']);
  const { byKey, isLoading } = useSegmentQueries(
    '/analytics/series', body, { granularity: 'day' }, segs, { parrainageOn: true, seuil });

  const data = useMemo(() => {
    const map = new Map();
    segs.forEach((k) => {
      (byKey[k]?.data?.series || []).forEach((pt) => {
        const row = map.get(pt.period) || { period: pt.period };
        row[k] = pt.bs_reel;
        map.set(pt.period, row);
      });
    });
    return [...map.values()].sort((a, b) => String(a.period).localeCompare(String(b.period)));
  }, [byKey, segs]);

  const series = segs.map((k) => ({ key: k, name: SEG_META[k].label, color: SEG_META[k].color }));
  const hasData = data.some((row) => segs.some((k) => (row[k] || 0) > 0));

  return (
    <div className="space-y-3">
      <SegmentChips value={segs} onChange={setSegs} testid="trend-segment-chips" />
      {isLoading ? <CardSkeleton height="h-[260px]" />
        : hasData
          ? <TrendAreaChart data={data} series={series} xTickFormatter={tickDate}
              formatTip={(v) => fmtInt(v)} formatY={(v) => fmtInt(v)} />
          : <EmptyState body="Aucune signature sur la période pour cette sélection." />}
    </div>
  );
}

// ---------- Répartition par âge superposée par famille ----------
export function AgeOverlayView({ segs, overalls, emptyBody }) {
  // Une seule famille sélectionnée → donut classique + âge moyen.
  if (segs.length === 1) {
    const d = overalls[segs[0]];
    if (!d || !(d.total > 0)) return <EmptyState body={emptyBody || "Aucune donnée d'âge sur la période."} />;
    return (
      <div className="space-y-3">
        <AgeDonut data={d.age_distribution} />
        <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm">
          <span className="text-muted-foreground">Âge moyen · {SEG_META[segs[0]].short}</span>
          <span className="font-semibold tabular" data-testid="age-overlay-mean">{d.age_moyen ? `${fmtNum(d.age_moyen, 1)} ans` : '—'}</span>
        </div>
      </div>
    );
  }

  // Plusieurs familles → barres groupées par tranche d'âge (en % pour comparer).
  const ref = segs.map((k) => overalls[k]).find((o) => o?.age_distribution?.length);
  const labels = (ref?.age_distribution || []).map((d) => d.label);
  const data = labels.map((label) => {
    const row = { bucket: label };
    segs.forEach((k) => {
      const dd = overalls[k]?.age_distribution?.find((x) => x.label === label);
      row[k] = dd ? Math.round((dd.pct || 0) * 1000) / 10 : 0;
    });
    return row;
  });
  const series = segs.map((k) => ({ key: k, name: SEG_META[k].label, color: SEG_META[k].color }));
  const anyData = segs.some((k) => overalls[k]?.total > 0);
  if (!anyData) return <EmptyState body={emptyBody || "Aucune donnée d'âge sur la période."} />;

  return (
    <div className="space-y-3">
      <ComparisonBars data={data} xKey="bucket" series={series} height={280} formatTip={(v) => `${fmtNum(v, 1)} %`} />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {segs.map((k) => (
          <div key={k} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-xs" data-testid={`age-mean-${k}`}>
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ background: SEG_META[k].color }} />
              {SEG_META[k].short}
            </span>
            <span className="font-semibold tabular">{overalls[k]?.age_moyen ? `${fmtNum(overalls[k].age_moyen, 1)} ans` : '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SegmentAge({ body, seuil }) {
  const [segs, setSegs] = useState(['ensemble']);
  const { byKey, isLoading } = useSegmentQueries(
    '/analytics/age-distribution', body, {}, segs, { parrainageOn: true, seuil });
  const overalls = {
    ensemble: byKey.ensemble?.data,
    regulier: byKey.regulier?.data,
    parrainage: byKey.parrainage?.data,
  };
  return (
    <div className="space-y-3">
      <SegmentChips value={segs} onChange={setSegs} testid="age-segment-chips" />
      {isLoading ? <CardSkeleton height="h-[260px]" /> : <AgeOverlayView segs={segs} overalls={overalls} />}
    </div>
  );
}
